import type { MinigameId } from '@minigame-race/contracts';
import { useState, useEffect, useRef } from 'react';
import { Avatar, Screen } from '../components/Chrome.js';
import { HelpButton, HelpDialog } from '../components/Help.js';
import { SegmentRevealOverlay } from '../race/SegmentReveal.js';
import { ReactionFloats, EmoteButton } from '../race/Reactions.js';
import { formatClock } from '../lib/useSmoothClock.js';
import type { GameState } from '../lib/useGame.js';
import { MinigameHost } from '../race/MinigameHost.js';
import { MINIGAME_PROGRESS, MINIGAME_ACCENT } from '../race/registry.js';
import { color } from '../theme.js';
import { play as playSound } from '../lib/sound.js';

/** Below this the HUD goes red and starts blinking. */
const CRITICAL_MS = 10_000;

/**
 * How many hints the opponent has bought, if their current game reports any.
 *
 * `view` is the opaque publicView() of whatever minigame they're on, so this
 * duck-types rather than assuming Wordle. A game with no hints simply has no
 * `hintsUsed` field and we render nothing.
 */
function opponentHintsUsed(view: unknown): number {
  if (typeof view !== 'object' || view === null) return 0;
  const n = (view as { hintsUsed?: unknown }).hintsUsed;
  return typeof n === 'number' && n > 0 ? n : 0;
}

type Mode = 'normal' | 'grace' | 'critical';

/**
 * The core screen. Global clock, the mounted minigame, and the opponent rail.
 *
 * The clock here is display-only: it interpolates between the server's 10Hz
 * samples so it ticks at frame rate, but the server owns the truth.
 */
export function RaceScreen({
  state,
  remainingMs,
  sendEvent,
  sendReaction,
  elapsedMs,
}: {
  state: GameState;
  /** Counts the cap down; once someone finishes, the grace window down. */
  remainingMs: number;
  sendEvent: (matchId: string, segmentIndex: number, event: unknown) => void;
  /** Fire an emote in the current match. */
  sendReaction: (matchId: string, reaction: string) => void;
  /** Whole-race elapsed, for the header stat. */
  elapsedMs: number;
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  const race = state.race;
  if (!race) return null;

  // Tick on each of the last few seconds before the cap/grace deadline.
  const lastTickSec = useRef<number | null>(null);
  useEffect(() => {
    const sec = Math.ceil(remainingMs / 1000);
    if (remainingMs > 0 && remainingMs <= 5000 && lastTickSec.current !== sec) {
      lastTickSec.current = sec;
      playSound('tick');
    }
    if (remainingMs > 5000) lastTickSec.current = null;
  }, [remainingMs]);

  // The clock counts the cap DOWN. Once someone finishes, it's the grace
  // window counting down instead. Either way it's `remainingMs`.
  const capImminent = !race.graceActive && remainingMs <= CRITICAL_MS;
  const mode: Mode = race.graceActive ? 'grace' : capImminent ? 'critical' : 'normal';

  const clockText = race.graceActive
    ? `${Math.ceil(remainingMs / 1000)}`
    : formatClock(remainingMs);

  const you = state.match?.you;
  const opponentName = state.match?.opponent.displayName ?? 'OPPONENT';

  const clockTint =
    mode === 'critical' ? color.redBright : mode === 'grace' ? color.amberBright : color.lime;
  const clockBorder =
    mode === 'critical' ? 'rgba(255,46,77,.6)' : mode === 'grace' ? 'rgba(255,176,32,.55)' : 'rgba(166,255,46,.32)';
  const clockBg =
    mode === 'critical' ? 'rgba(255,46,77,.12)' : mode === 'grace' ? 'rgba(255,176,32,.1)' : 'rgba(166,255,46,.06)';

  const totalSegments = race.recipe.length;

  // Your marker must advance with every tap and every guess, exactly as the
  // opponent's does. `segmentIndex` alone counts only FINISHED segments, so it
  // used to sit still for a whole minigame and then jump — while the opponent's
  // marker, whose fraction the server computes for us, crept forward smoothly.
  //
  // Mirrors the server's overallProgressPct: completed segments + the fraction
  // of the one in progress.
  const yourSegmentFraction = race.finished
    ? 0
    : MINIGAME_PROGRESS[race.minigameId](race.view);
  const yourProgressPct =
    totalSegments === 0
      ? 100
      : Math.min(100, ((race.segmentIndex + yourSegmentFraction) / totalSegments) * 100);

  return (
    <Screen>
      <ReactionFloats reactions={state.reactions} />
      {race.reveal && <SegmentRevealOverlay reveal={race.reveal} onReact={(r) => sendReaction(race.matchId, r)} />}
      {helpOpen && <HelpDialog minigameId={race.minigameId} onClose={() => setHelpOpen(false)} />}

      {/* mode vignettes */}
      {mode === 'grace' && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 4,
            boxShadow: 'inset 0 0 clamp(60px,18cqmin,220px) rgba(255,176,32,.3)',
            animation: 'rhVigAmber 1.3s ease-in-out infinite',
          }}
        />
      )}
      {mode === 'critical' && (
        <>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 4,
              boxShadow: 'inset 0 0 clamp(50px,16cqmin,200px) rgba(255,46,77,.42)',
              animation: 'rhVigRed .62s ease-in-out infinite',
            }}
          />
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 4,
              background: 'rgba(255,46,77,.07)',
              animation: 'rhFlash .62s steps(1,end) infinite',
            }}
          />
        </>
      )}

      {/* banners */}
      {mode === 'grace' && (
        <div
          style={{
            position: 'absolute',
            top: 'clamp(74px,15cqmin,120px)',
            left: '50%',
            transform: 'translate(-50%,0)',
            zIndex: 7,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            maxWidth: 'min(92cqw,520px)',
            padding: 'clamp(9px,1.7cqmin,15px) clamp(16px,3cqmin,30px)',
            border: `1px solid ${race.youSolved ? 'rgba(166,255,46,.5)' : 'rgba(255,176,32,.65)'}`,
            borderRadius: 16,
            background: race.youSolved
              ? 'linear-gradient(180deg,rgba(16,32,10,.95),rgba(11,22,7,.95))'
              : 'linear-gradient(180deg,rgba(38,26,6,.95),rgba(28,19,4,.95))',
            animation: 'rhBannerIn .35s ease-out, rhBannerPulse 1.1s ease-in-out infinite',
            textAlign: 'center',
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 'clamp(11px,2.1cqmin,17px)',
              letterSpacing: '.06em',
              color: race.youSolved ? color.lime : color.amberBright,
            }}
          >
            {race.youSolved
              ? 'YOU SOLVED IT — WAITING'
              : `${opponentName} SOLVED IT`}
          </span>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <span
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontWeight: 800,
                fontSize: 'clamp(22px,4.6cqmin,38px)',
                lineHeight: 1,
                color: '#fff',
                textShadow: race.youSolved ? undefined : `0 0 14px ${color.amberBright}88`,
              }}
            >
              {(remainingMs / 1000).toFixed(1)}s
            </span>
            <span style={{ fontSize: 'clamp(9px,1.6cqmin,12px)', letterSpacing: '.14em', color: 'rgba(234,255,234,.5)' }}>
              {race.youSolved ? 'FOR THEM' : 'LEFT'}
            </span>
          </div>

          {!race.youSolved && (
            <span style={{ fontSize: 'clamp(9px,1.6cqmin,12px)', color: 'rgba(234,255,234,.55)' }}>
              Finish now or take the penalty
            </span>
          )}
        </div>
      )}

      {mode === 'critical' && (
        <div
          style={{
            position: 'absolute',
            top: 'clamp(74px,15cqmin,120px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 7,
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(7px,1.4cqmin,12px)',
            padding: 'clamp(6px,1.3cqmin,12px) clamp(13px,2.6cqmin,26px)',
            border: '1px solid rgba(255,46,77,.65)',
            borderRadius: 999,
            background: 'linear-gradient(180deg,rgba(40,7,13,.94),rgba(28,5,9,.94))',
            animation: 'rhTagPulse .62s ease-in-out infinite',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontWeight: 800, fontSize: 'clamp(11px,2.3cqmin,20px)', letterSpacing: '.12em', color: color.redBright }}>
            ⚠ TIME ALMOST OUT
          </span>
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 'clamp(10px,2cqmin,17px)', color: 'rgba(234,255,234,.75)' }}>
            {Math.ceil(remainingMs / 1000)}s — THIS GAME WILL CLOSE
          </span>
        </div>
      )}

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: 'clamp(16px,3.4cqmin,38px)',
          gap: 'clamp(10px,2cqmin,22px)',
        }}
      >
        {/* ===== top bar ===== */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px,1.6cqmin,15px)' }}>
            <Avatar name={you?.displayName ?? 'You'} side="you" size={48} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 'clamp(14px,2.7cqmin,23px)', letterSpacing: '.06em' }}>YOU</span>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, fontSize: 'clamp(9px,1.6cqmin,13px)', color: 'rgba(166,255,46,.75)' }}>
                  {you?.elo ?? 0} ELO
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: '.22em', color: 'rgba(234,255,234,.4)', fontWeight: 600 }}>ELAPSED</span>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 'clamp(11px,2cqmin,17px)', color: 'rgba(234,255,234,.85)' }}>
                  {formatClock(elapsedMs)}
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, animation: mode === 'grace' ? 'rhPulseAmber 1.1s ease-in-out infinite' : undefined }}>
            <span
              style={{
                fontSize: 'clamp(7px,1.3cqmin,11px)',
                letterSpacing: mode === 'critical' ? '.3em' : '.28em',
                fontWeight: mode === 'normal' ? 600 : 700,
                color: mode === 'critical' ? color.red : mode === 'grace' ? color.amber : 'rgba(166,255,46,.65)',
              }}
            >
              {mode === 'critical' ? '⚠ TIME' : mode === 'grace' ? 'GRACE · CLOSE IT' : 'RACE CLOCK'}
            </span>
            <div
              style={{
                padding: 'clamp(4px,1cqmin,10px) clamp(11px,2.2cqmin,22px)',
                border: `1px solid ${clockBorder}`,
                borderRadius: 'clamp(6px,1.4cqmin,12px)',
                background: clockBg,
                boxShadow: 'inset 0 0 clamp(8px,2cqmin,22px) rgba(166,255,46,.15)',
                animation: mode === 'critical' ? 'rhBlinkRed .62s steps(1,end) infinite' : undefined,
              }}
            >
              <span
                style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontWeight: 800,
                  fontSize: 'clamp(30px,7.6cqmin,66px)',
                  lineHeight: 1,
                  color: clockTint,
                  textShadow: `0 0 clamp(6px,2cqmin,20px) ${clockTint}88`,
                }}
              >
                {clockText}
              </span>
            </div>

          </div>
        </div>

        {/* ===== the minigame ===== */}
        {/*
          The "?" is a laid-out row above the board, not an overlay pinned to the
          corner. Absolutely positioning it would put it on top of the board on a
          narrow screen — directly over Tile Tap's top-right tile.
        */}
        {!race.finished && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'clamp(6px,1.4cqmin,10px)', flexShrink: 0, marginBottom: -6 }}>
            <EmoteButton onSend={(r) => sendReaction(race.matchId, r)} />
            <HelpButton onClick={() => setHelpOpen(true)} />
          </div>
        )}
        <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          {/*
            A faint per-game accent glow behind the board, so consecutive
            segments read as visually distinct without touching the lime/cyan
            "you vs rival" language. Purely cosmetic, non-interactive.
          */}
          {!race.finished && (
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                background: `radial-gradient(60% 55% at 50% 45%, rgba(${MINIGAME_ACCENT[race.minigameId]},.12), transparent 70%)`,
                transition: 'background .4s ease',
              }}
            />
          )}
          {race.finished ? (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={{ fontSize: 'clamp(24px,6cqmin,52px)', fontWeight: 700, color: color.lime, letterSpacing: '.04em', animation: 'rsGlowW 2s ease-in-out infinite' }}>
                RACE COMPLETE
              </span>
              <span style={{ fontSize: 'clamp(10px,1.9cqmin,16px)', color: 'rgba(234,255,234,.5)' }}>Waiting for your opponent to close…</span>
            </div>
          ) : (
            <MinigameHost
              minigameId={race.minigameId}
              matchId={race.matchId}
              segmentIndex={race.segmentIndex}
              view={race.view}
              opponentView={race.opponent.view}
              sendEvent={sendEvent}
              rejectNonce={state.rejectNonce}
              lastReject={state.lastReject}
            />
          )}
        </div>

        {/* ===== race track ===== */}
        <RaceTrack
          recipe={race.recipe}
          yourSegment={race.segmentIndex}
          yourFinished={race.finished}
          yourPct={race.finished ? 100 : yourProgressPct}
          oppPct={race.opponent.progressPct}
          oppFinished={race.opponent.finished}
          oppHints={opponentHintsUsed(race.opponent.view)}
        />
      </div>
    </Screen>
  );
}

/**
 * The progress rail: stations for each segment, a lime marker above the line for
 * you and a cyan one below for the opponent.
 */
function RaceTrack({
  recipe,
  yourSegment,
  yourFinished,
  yourPct,
  oppPct,
  oppFinished,
  oppHints,
}: {
  recipe: readonly MinigameId[];
  yourSegment: number;
  yourFinished: boolean;
  yourPct: number;
  oppPct: number;
  oppFinished: boolean;
  oppHints: number;
}) {
  const n = recipe.length;
  // Stations sit at 8%..92%, with a finish flag at the end.
  const stationX = (i: number): number => 8 + (i * (92 - 8)) / n;
  const finishX = 92;
  const markerX = (pct: number): number => 8 + (pct / 100) * (finishX - 8);

  return (
    <div style={{ borderTop: '1px solid rgba(166,255,46,.12)', paddingTop: 'clamp(9px,1.8cqmin,18px)', display: 'flex', flexDirection: 'column', gap: 'clamp(7px,1.5cqmin,15px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 'clamp(7px,1.4cqmin,12px)', letterSpacing: '.24em', fontWeight: 600, color: 'rgba(234,255,234,.45)' }}>RACE PROGRESS</span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px,1.4cqmin,12px)' }}>
          {/* The opponent bought a hint. A bare count — it says "they're
              struggling" and reveals nothing about the word. */}
          {oppHints > 0 && (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 9px',
                border: '1px solid rgba(34,224,255,.35)',
                borderRadius: 999,
                background: 'rgba(34,224,255,.08)',
                fontSize: 'clamp(7px,1.3cqmin,11px)',
                letterSpacing: '.1em',
                fontWeight: 700,
                color: color.cyanBright,
                whiteSpace: 'nowrap',
              }}
            >
              💡 OPP ×{oppHints}
            </span>
          )}
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 'clamp(11px,2.1cqmin,18px)', letterSpacing: '.05em' }}>
            GAME <span style={{ color: color.lime }}>{String(Math.min(yourSegment + 1, n)).padStart(2, '0')}</span> / {String(n).padStart(2, '0')}
          </span>
        </div>
      </div>

      <div style={{ position: 'relative', height: 'clamp(42px,9cqmin,68px)' }}>
        <div style={{ position: 'absolute', left: '3%', right: '3%', top: '50%', height: 2, transform: 'translateY(-50%)', background: 'rgba(234,255,234,.12)' }} />
        <div
          style={{
            position: 'absolute',
            left: '3%',
            width: `${Math.max(0, markerX(yourPct) - 3)}%`,
            top: '50%',
            height: 'clamp(3px,.7cqmin,5px)',
            transform: 'translateY(-50%)',
            background: `linear-gradient(90deg,rgba(166,255,46,.3),${color.lime})`,
            borderRadius: 2,
            boxShadow: '0 0 clamp(6px,1.6cqmin,16px) rgba(166,255,46,.6)',
            // Snappier than the opponent's rail: our progress updates on every
            // accepted event (~1 RTT), theirs on the server's 100ms tick. A
            // 150ms ease here would lag behind a fast tapper's own clicks.
            transition: 'width .08s linear',
          }}
        />

        {recipe.map((_, i) => {
          const done = i < yourSegment || yourFinished;
          const current = i === yourSegment && !yourFinished;
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: `${stationX(i)}%`,
                top: '50%',
                transform: 'translate(-50%,-50%)',
                width: current ? 'clamp(24px,5.4cqmin,46px)' : 'clamp(20px,4.6cqmin,40px)',
                height: current ? 'clamp(24px,5.4cqmin,46px)' : 'clamp(20px,4.6cqmin,40px)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: done ? color.lime : current ? 'rgba(166,255,46,.12)' : 'rgba(255,255,255,.04)',
                border: current ? `clamp(2px,.5cqmin,3px) solid ${color.lime}` : done ? 'none' : '1px solid rgba(234,255,234,.16)',
                color: done ? color.limeInk : current ? color.limeBright : 'rgba(234,255,234,.4)',
                fontFamily: "'JetBrains Mono',monospace",
                fontWeight: 800,
                fontSize: 'clamp(9px,2cqmin,16px)',
                boxShadow: done || current ? '0 0 clamp(8px,2.2cqmin,24px) rgba(166,255,46,.5)' : undefined,
              }}
            >
              {done ? '✓' : i + 1}
            </div>
          );
        })}

        <div
          style={{
            position: 'absolute',
            left: `${finishX}%`,
            top: '50%',
            transform: 'translate(-50%,-50%)',
            width: 'clamp(20px,4.6cqmin,40px)',
            height: 'clamp(20px,4.6cqmin,40px)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,.04)',
            border: '1px dashed rgba(234,255,234,.28)',
            color: 'rgba(234,255,234,.5)',
            fontSize: 'clamp(8px,1.7cqmin,14px)',
          }}
        >
          ⚑
        </div>

        {/* YOU — above the line. Advances on every tap/guess, not just on
            finishing a whole minigame. */}
        <div
          style={{
            position: 'absolute',
            left: `${markerX(yourPct)}%`,
            top: '50%',
            transform: 'translate(-50%,calc(-50% - clamp(12px,2.8cqmin,24px)))',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            zIndex: 4,
            transition: 'left .08s linear',
          }}
        >
          <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: '.14em', fontWeight: 700, color: color.lime, textShadow: '0 0 6px rgba(166,255,46,.5)' }}>YOU</span>
          <div style={{ width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: `10px solid ${color.lime}`, filter: 'drop-shadow(0 0 4px rgba(166,255,46,.7))' }} />
        </div>

        {/* OPPONENT — below the line */}
        <div
          style={{
            position: 'absolute',
            left: `${oppFinished ? finishX : markerX(oppPct)}%`,
            top: '50%',
            transform: 'translate(-50%,calc(-50% + clamp(11px,2.6cqmin,22px)))',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            zIndex: 5,
            transition: 'left .15s linear',
            animation: oppFinished ? 'rhOppFin 1s ease-in-out infinite' : undefined,
          }}
        >
          <div style={{ width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderBottom: `10px solid ${color.cyan}`, filter: `drop-shadow(0 0 ${oppFinished ? 7 : 4}px rgba(34,224,255,.9))` }} />
          <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: oppFinished ? '.1em' : '.14em', fontWeight: oppFinished ? 800 : 700, color: oppFinished ? color.cyanBright : color.cyan, textShadow: '0 0 6px rgba(34,224,255,.5)' }}>
            {oppFinished ? 'OPP ✓ FIN' : 'OPP'}
          </span>
        </div>
      </div>
    </div>
  );
}
